import { MediaImage, MediaLink } from "@/components/admin/MediaAsset";
import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Trash2, Copy, Loader2, ImageIcon, Check, FolderOpen, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const fileUrl = (path) => `${process.env.REACT_APP_BACKEND_URL || ""}/api/files/${path}`;

export default function MediaGallery({ picker = false, multi = false, onPick, readOnly = false }) {
  const [selected, setSelected] = useState([]);
  const pick = (url) => {
    if (!multi) { onPick(url); return; }
    if (selected.includes(url)) { setSelected((items) => items.filter((item) => item !== url)); return; }
    if (selected.length >= 10) { toast.error("Maksimal 10 gambar per posting"); return; }
    setSelected((items) => [...items, url]);
  };
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("all");
  const [uploadFolder, setUploadFolder] = useState("Umum");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = folder !== "all" ? { folder } : {};
      setMedia((await api.get("/media", { params })).data);
      setFolders((await api.get("/media/folders")).data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [folder]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", uploadFolder || "Umum");
      await api.post("/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Media terunggah");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const remove = async (id) => {
    await api.delete(`/media/${id}`);
    toast.success("Media dihapus"); load();
  };

  const copy = (path) => {
    navigator.clipboard.writeText(fileUrl(path));
    setCopied(path); toast.success("URL disalin");
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex min-w-0 w-full flex-wrap items-center gap-2 sm:w-auto">
          <Select value={folder} onValueChange={setFolder}>
            <SelectTrigger className="bg-[#FFFFFF] border-[#E2E8F0] w-full sm:w-[180px]" data-testid="media-folder-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900">
              <SelectItem value="all">Semua Grup</SelectItem>
              {folders.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
            </SelectContent>
          </Select>
          <span className="text-sm text-slate-500">{media.length} gambar</span>
        </div>
        {!readOnly && <div className="flex min-w-0 w-full items-center gap-2 sm:w-auto">
          <Input value={uploadFolder} onChange={(e) => setUploadFolder(e.target.value)} data-testid="media-upload-folder" className="bg-[#FFFFFF] border-[#E2E8F0] w-full min-w-0 flex-1 sm:w-[150px]" placeholder="Grup (mis. Promosi)" />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-file-input" />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="media-upload-button" className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl">
            {uploading ? <Loader2 size={16} className="animate-spin mr-1" /> : <Upload size={16} className="mr-1" />} Unggah
          </Button>
        </div>}
      </div>

      {loading ? (
        <div className="text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Memuat...</div>
      ) : media.length === 0 ? (
        <div className="bg-[#FFFFFF] border border-dashed border-[#E2E8F0] rounded-2xl p-6 sm:p-12 text-center">
          <ImageIcon size={32} className="mx-auto text-slate-600 mb-3" />
          <p className="text-slate-600 mb-4">{readOnly ? "Belum ada media milik Anda." : "Belum ada media. Unggah gambar pertama Anda."}</p>
          {!readOnly && <Button onClick={() => fileRef.current?.click()} className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl"><Upload size={16} className="mr-1" /> Unggah Gambar</Button>}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-4">
          {media.map((m) => (
            <div key={m.id} data-testid={`media-item-${m.id}`} className="group relative bg-[#FFFFFF] border border-[#E2E8F0] rounded-xl overflow-hidden hover:border-[#3B82F6] transition-colors">
              <div className="aspect-square bg-[#F1F5F9] flex items-center justify-center">
                <MediaImage src={fileUrl(m.storage_path)} alt={m.original_filename || "media"} className="w-full h-full object-cover" />
              </div>
              <div className="absolute inset-0 bg-[#F8FAFC]/20 sm:bg-[#F8FAFC]/80 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 [@media(hover:none)]:bg-[#F8FAFC]/20 transition-opacity flex items-center justify-center gap-2">
                {picker ? (
                  <Button size="sm" onClick={() => pick(fileUrl(m.storage_path))} aria-pressed={multi ? selected.includes(fileUrl(m.storage_path)) : undefined} data-testid={`media-pick-${m.id}`} className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-lg text-xs"><Check size={13} className="mr-1" />{selected.includes(fileUrl(m.storage_path)) ? `Dipilih ${selected.indexOf(fileUrl(m.storage_path)) + 1}` : "Pilih"}</Button>
                ) : (
                  <>
                    <MediaLink href={fileUrl(m.storage_path)} target="_blank" rel="noreferrer" aria-label="Buka gambar asli" title="Buka gambar asli" data-testid={`media-original-${m.id}`} className="w-9 h-9 rounded-lg bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-slate-800"><ExternalLink size={14} /></MediaLink>
                    <button aria-label="Salin URL gambar" onClick={() => copy(m.storage_path)} className="w-9 h-9 rounded-lg bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-slate-800">{copied === m.storage_path ? <Check size={14} className="text-[#059669]" /> : <Copy size={14} />}</button>
                    {!readOnly && <button aria-label="Hapus gambar" onClick={() => remove(m.id)} data-testid={`media-delete-${m.id}`} className="w-9 h-9 rounded-lg bg-[#F1F5F9] hover:bg-[#E2E8F0] flex items-center justify-center text-slate-800 hover:text-[#E11D48]"><Trash2 size={14} /></button>}
                  </>
                )}
              </div>
              <p className="text-[10px] text-slate-500 px-2 py-1 truncate flex items-center gap-1"><FolderOpen size={10} />{m.folder || "Umum"} · {(m.size / 1024).toFixed(0)} KB</p>
            </div>
          ))}
        </div>
      )}
      {picker && multi && <div className="sticky bottom-0 bg-[#FFFFFF] border-t border-[#E2E8F0] p-3 mt-4 flex flex-wrap items-center justify-between gap-2"><span className="text-xs text-slate-600">{selected.length}/10 gambar dipilih</span><Button type="button" data-testid="media-multi-confirm" onClick={() => onPick(selected)} className="bg-[#2563EB] hover:bg-[#1D4ED8]">Gunakan Gambar</Button></div>}
    </div>
  );
}
